/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Modal, Spin, Tooltip } from 'antd'
import {
  CloseOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import React, { useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import QuestionIcon from 'src/components/icons/QuestionIcon'
import { useIsOverflowing } from 'src/hooks/useIsOverflowing'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
  paragraphMedium,
  paragraphLarge,
  paragraphXSmall,
} from 'src/components/css'
import { useFamilyMembers } from '../hooks/useFamilyMembers'
import { useFamilyActions } from './useFamilyActions'
import type { FamilyScope } from '@sportninja/common/services/player/familyScope'
import type { FamilyContactPlayerSource } from './components/toFamilyContactPlayer'
import FamilyMemberRow from './components/FamilyMemberRow'
import { FAMILY_SUPPORT_URL } from '@sportninja/common/features/profile/family/constants'

interface Props {
  isOpen: boolean
  onClose: () => void
  playerId: string | null
  /**
   * Whether the current user is PRIMARY (admin) for this player. Only then are
   * the per-row edit/remove actions shown — a non-primary can view the family
   * but not manage it.
   */
  canManage?: boolean
  /**
   * Which backend routes to read/write through. Omitted → the self-scoped
   * /users/me/players/:id/family routes (the profile screen). The team roster
   * passes a 'roster' scope, whose access comes from team-admin permission
   * rather than the player being linked to the current user.
   */
  scope?: FamilyScope
  /**
   * Player for the invite/edit card, when the caller already has it. Supplying
   * it avoids a self-scoped player fetch the roster's caller cannot make.
   */
  player?: FamilyContactPlayerSource | null
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
}

/**
 * Family Management modal — opened from the player header's family icon button.
 * Hosts the grouped family list, the Invite Family Member action, and the
 * invite / edit / remove sub-modals (which stack on top and leave this open).
 */
const FamilyManagementModal: React.FC<Props> = ({
  isOpen,
  onClose,
  playerId,
  canManage = false,
  scope,
  player,
}) => {
  'use memo'
  const { groups, isLoading, error, refetch } = useFamilyMembers(
    playerId ?? '',
    scope
  )
  const { openInvite, handleEdit, handleDelete, handleResend, modals } =
    useFamilyActions(playerId, scope, player)

  // Always pull fresh data when the modal opens — the list can go stale while
  // closed (a member accepted, was edited, etc.).
  useEffect(() => {
    if (isOpen && playerId) {
      refetch()
    }
  }, [isOpen, playerId, refetch])

  // Add right padding only when the list scrolls, so rows clear the scrollbar
  // without leaving dead space on the right when everything fits.
  const [listRef, isOverflowing] = useIsOverflowing<HTMLDivElement>([
    isOpen,
    groups,
  ])

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const header = (
    <div css={headerRowStyle}>
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords('Family Management')}
      </Text>
      <Tooltip
        title={capitalizeWords('How Family Management Works')}
        zIndex={10002}
      >
        <a
          css={infoButtonStyle}
          href={FAMILY_SUPPORT_URL}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Learn more about family management'
        >
          <QuestionIcon size={22} color={colors.PRIMARY} />
        </a>
      </Tooltip>
    </div>
  )

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Modal
        css={modalCss}
        width={600}
        closeIcon={closeIcon}
        title={header}
        footer={null}
        open={isOpen}
        onCancel={onClose}
        centered
        styles={modalStyles}
      >
        <div css={bodyStyle}>
          {/* Scrollable list region — caps height and scrolls when there are
              many members, keeping the Invite button pinned below. */}
          <div
            ref={listRef}
            css={[listScrollStyle, isOverflowing && listScrollOverflowStyle]}
          >
            {isLoading ? (
              <div css={centeredStyle}>
                <Spin />
              </div>
            ) : error ? (
              <div css={centeredStyle}>
                <span css={errorTextStyle}>
                  Couldn&apos;t load family members.
                </span>
              </div>
            ) : groups.length === 0 ? (
              <div css={emptyStateStyle}>
                <div css={emptyIconWrapStyle}>
                  <TeamOutlined css={emptyIconStyle} />
                </div>
                <span css={emptyTextStyle}>
                  {capitalizeWords('No family members yet')}
                </span>
                <span css={emptyHintStyle}>
                  For more information about this feature,{' '}
                  <a
                    css={emptyLinkStyle}
                    href={FAMILY_SUPPORT_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Click Here.
                  </a>
                </span>
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.group} css={groupSectionStyle}>
                  <span css={sectionLabelStyle}>{g.title}</span>
                  <div css={memberListStyle}>
                    {g.members.map((member) => (
                      <FamilyMemberRow
                        key={member.id}
                        member={member}
                        showActions={canManage}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onResend={handleResend}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Invite is a management action — only a primary (admin) for this
              player may add family members. */}
          {canManage ? (
            <button css={inviteButtonStyle} type='button' onClick={openInvite}>
              <UsergroupAddOutlined css={inviteIconStyle} />
              <span css={inviteLabelStyle}>
                {capitalizeWords('Invite Family Member')}
              </span>
            </button>
          ) : null}
        </div>
      </Modal>

      {modals}
    </>
  )
}

export default FamilyManagementModal

// ─── Styles ────────────────────────────────────────────────────────────────

const modalCss = css`
  display: flex;
  flex: 1;
  max-height: 90vh;
  margin: 24px auto;
  padding: 0;

  .ant-modal-content {
    max-height: 90vh;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const infoButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  flex: 1;
  min-height: 0;
`

// Only the member list scrolls; the Invite button below stays pinned.
const listScrollStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  min-height: 0;
  /* Cap so ~5-6 rows show before scrolling; modal still respects 90vh too. */
  max-height: 420px;
`

// Applied only when the list actually overflows, so rows clear the scrollbar
// without adding dead space on the right when everything fits.
const listScrollOverflowStyle = css`
  padding-right: 12px;
`

const groupSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const memberListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const sectionLabelStyle = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_100};
  display: block;
`

const inviteButtonStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    opacity: 0.85;
  }
`

const inviteIconStyle = css`
  font-size: 16px;
  color: ${colors.NEUTRAL_0};
`

const inviteLabelStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

const centeredStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`

const emptyStateStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
`

const emptyIconWrapStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: ${colors.NEUTRAL_700};
`

const emptyIconStyle = css`
  font-size: 26px;
  color: ${colors.NEUTRAL_50};
`

const errorTextStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_100};
`

const emptyTextStyle = css`
  ${paragraphLarge}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

const emptyHintStyle = css`
  ${paragraphMedium}
  ${font.body}
  color: ${colors.NEUTRAL_50};
`

const emptyLinkStyle = css`
  ${weightMedium}
  color: ${colors.PRIMARY};
  text-decoration: none;

  &:hover {
    color: ${colors.PRIMARY} !important;
    opacity: 0.7;
  }
`
